import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface ParagraphBlockMapping {
  [paragraphId: string]: {
    blockId: string
    content: string
    order: number
    lastUpdated: number
  }
}

export interface ParagraphBlockSyncState {
  paragraphBlockMap: ParagraphBlockMapping
  isSyncing: boolean
  lastSyncTime: number
  error: string | null
}

const initialState: ParagraphBlockSyncState = {
  paragraphBlockMap: {},
  isSyncing: false,
  lastSyncTime: 0,
  error: null
}

export const paragraphBlockSyncSlice = createSlice({
  name: 'paragraphBlockSync',
  initialState,
  reducers: {
    // 更新段落与块的映射关系
    updateMapping: (state, action: PayloadAction<{
      paragraphId: string
      blockId: string
      content: string
      order: number
      lastUpdated: number
    }>) => {
      const { paragraphId, blockId, content, order, lastUpdated } = action.payload
      
      // 更新映射关系
      state.paragraphBlockMap[paragraphId] = {
        blockId,
        content,
        order,
        lastUpdated
      }
      
      // 更新最后同步时间
      state.lastSyncTime = Date.now()
      state.error = null
    },
    
    // 批量更新映射关系
    batchUpdateMapping: (state, action: PayloadAction<Array<{
      paragraphId: string
      blockId: string
      content: string
      order: number
      lastUpdated: number
    }>>) => {
      action.payload.forEach(mapping => {
        state.paragraphBlockMap[mapping.paragraphId] = {
          blockId: mapping.blockId,
          content: mapping.content,
          order: mapping.order,
          lastUpdated: mapping.lastUpdated
        }
      })
      
      state.lastSyncTime = Date.now()
      state.error = null
    },
    
    // 删除映射关系
    removeMapping: (state, action: PayloadAction<string>) => {
      delete state.paragraphBlockMap[action.payload]
      state.lastSyncTime = Date.now()
    },
    
    // 清空映射关系
    clearMapping: (state) => {
      state.paragraphBlockMap = {}
      state.lastSyncTime = Date.now()
    },
    
    // 设置同步状态
    setSyncing: (state, action: PayloadAction<boolean>) => {
      state.isSyncing = action.payload
    },
    
    // 设置同步错误
    setSyncError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.isSyncing = false
    },
    
    // 从块ID获取段落ID
    getParagraphIdByBlockId: (state, action: PayloadAction<string>) => {
      // 这个reducer实际上是一个selector，它不会修改状态
      // 但我们可以在这里处理逻辑，然后通过其他方式返回结果
      const blockId = action.payload
      // 查找段落ID但不使用，因为这是reducer
      Object.keys(state.paragraphBlockMap).find(
        key => state.paragraphBlockMap[key].blockId === blockId
      )
      
      // 不返回任何值，因为这是reducer
    },
    
    // 从段落ID获取块ID
    getBlockIdByParagraphId: (_, action: PayloadAction<string>) => {
      // 同样是一个selector
      // 获取段落ID但不使用，因为这是reducer
      action.payload
      // 不返回任何值，因为这是reducer
    }
  },
  
  // 处理其他slice的action
  extraReducers: (builder) => {
    // 可以在这里添加对其他slice action的处理
    // 例如，当笔记切换时，清空映射关系
    builder
      .addMatcher(
        (action) => action.type.startsWith('notes/') && action.type.includes('setCurrentNote'),
        (state) => {
          state.paragraphBlockMap = {}
          state.lastSyncTime = Date.now()
        }
      )
  }
})

// 导出actions
export const {
  updateMapping,
  batchUpdateMapping,
  removeMapping,
  clearMapping,
  setSyncing,
  setSyncError,
  getParagraphIdByBlockId,
  getBlockIdByParagraphId
} = paragraphBlockSyncSlice.actions

// 导出selectors
export const selectParagraphBlockMap = (state: any) => state.paragraphBlockSync.paragraphBlockMap
export const selectIsSyncing = (state: any) => state.paragraphBlockSync.isSyncing
export const selectLastSyncTime = (state: any) => state.paragraphBlockSync.lastSyncTime
export const selectSyncError = (state: any) => state.paragraphBlockSync.error

// 从块ID获取段落ID的selector
export const selectParagraphIdByBlockId = (blockId: string) => (state: any) => {
  return Object.keys(state.paragraphBlockSync.paragraphBlockMap).find(
    key => state.paragraphBlockSync.paragraphBlockMap[key].blockId === blockId
  )
}

// 从段落ID获取块ID的selector
export const selectBlockIdByParagraphId = (paragraphId: string) => (state: any) => {
  return state.paragraphBlockSync.paragraphBlockMap[paragraphId]?.blockId
}

// 导出reducer
export default paragraphBlockSyncSlice.reducer
